import { getIdFromObj, getIriFromObj } from 'utils/processors';

export const TASK_TREE_MAX_DEPTH = 3;

const getTaskIdFromIri = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const match = value.match(/\/tplmgmt\/tasks\/(\d+)$/);
    return match ? Number(match[1]) : null;
};

const normalizeSiblingPosition = (task) => {
    const position = Number(task?.positionByParent);
    return Number.isFinite(position) ? position : Number.MAX_SAFE_INTEGER;
};

const getTaskName = (task) => task?.name || task?.code || '';

const moveItem = (items = [], fromIndex = -1, toIndex = -1) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
        return [...items];
    }

    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    return nextItems;
};

export const getTaskNodeId = (task, fallbackIndex = 0) => {
    if (typeof task?.treeNodeId === 'string' && task.treeNodeId.trim()) {
        return task.treeNodeId;
    }

    const iri = getIriFromObj(task);
    if (iri) {
        return iri;
    }

    const id = getIdFromObj(task);
    if (id !== undefined && id !== null) {
        if (typeof id === 'string' && id.trim()) {
            return id;
        }

        return `tpl-task:${id}`;
    }

    if (typeof task?.code === 'string' && task.code.trim()) {
        return `tpl-task-code:${task.code.trim()}`;
    }

    if (typeof task?.name === 'string' && task.name.trim()) {
        return `tpl-task-name:${task.name.trim()}:${fallbackIndex}`;
    }

    return `tpl-task-index:${fallbackIndex}`;
};

export const getTaskParentRef = (task) => {
    if (typeof task?.parent?.treeNodeId === 'string' && task.parent.treeNodeId.trim()) {
        return task.parent.treeNodeId;
    }

    const parentIdFromIri = getTaskIdFromIri(task?.parent);
    if (parentIdFromIri !== null) {
        return `tpl-task:${parentIdFromIri}`;
    }

    const parentId = getIdFromObj(task?.parent);
    if (parentId !== undefined && parentId !== null) {
        if (typeof parentId === 'string' && parentId.trim()) {
            return `tpl-task:${parentId}`;
        }

        return `tpl-task:${parentId}`;
    }

    const iri = getIriFromObj(task?.parent);
    if (iri) {
        return iri;
    }

    if (typeof task?.parent === 'number') {
        return `tpl-task:${task.parent}`;
    }

    return typeof task?.parent === 'string' && task.parent.trim() ? task.parent : null;
};

export const isRootTask = (task) => !getTaskParentRef(task);

const compareSiblingTasks = (metaA, metaB) => {
    const positionDiff = normalizeSiblingPosition(metaA.task) - normalizeSiblingPosition(metaB.task);
    if (positionDiff !== 0) {
        return positionDiff;
    }

    const nameCompare = getTaskName(metaA.task).localeCompare(getTaskName(metaB.task));
    if (nameCompare !== 0) {
        return nameCompare;
    }

    return metaA.flatIndex - metaB.flatIndex;
};

const buildTaskMetadata = (tasks = []) => {
    const sourceTasks = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
    const metas = sourceTasks.map((task, flatIndex) => ({
        task,
        flatIndex,
        nodeId: getTaskNodeId(task, flatIndex),
        parentRef: getTaskParentRef(task)
    }));

    const metasByNodeId = new Map(metas.map((meta) => [meta.nodeId, meta]));
    const childrenByParent = new Map();

    metas.forEach((meta) => {
        const normalizedParentRef = meta.parentRef && metasByNodeId.has(meta.parentRef) ? meta.parentRef : null;
        meta.parentNodeId = normalizedParentRef;

        if (!childrenByParent.has(normalizedParentRef)) {
            childrenByParent.set(normalizedParentRef, []);
        }

        childrenByParent.get(normalizedParentRef).push(meta);
    });

    childrenByParent.forEach((siblings) => siblings.sort(compareSiblingTasks));

    return { childrenByParent };
};

const resolveTreePath = (meta, inheritedPath) => [...inheritedPath, meta.nodeId];

export const sortTasksByParentPosition = (tasks = []) => {
    const { childrenByParent } = buildTaskMetadata(tasks);
    const orderedMetas = [];

    const walk = (parentNodeId = null) => {
        const siblings = childrenByParent.get(parentNodeId) || [];

        siblings.forEach((meta) => {
            orderedMetas.push(meta);
            walk(meta.nodeId);
        });
    };

    walk(null);

    return orderedMetas.map((meta) => meta.task);
};

export const buildTaskTreeRows = (tasks = []) => {
    const { childrenByParent } = buildTaskMetadata(tasks);
    const treeRows = [];

    const walk = (parentNodeId = null, inheritedPath = []) => {
        const siblings = childrenByParent.get(parentNodeId) || [];

        siblings.forEach((meta) => {
            const treePath = resolveTreePath(meta, inheritedPath);

            treeRows.push({
                ...meta.task,
                id: meta.task?.id ?? meta.nodeId,
                treePath,
                treeNodeId: meta.nodeId,
                flatIndex: meta.flatIndex,
                treeDepth: Math.max(treePath.length - 1, 0),
                isRoot: parentNodeId === null
            });

            walk(meta.nodeId, treePath);
        });
    };

    walk(null);

    return treeRows;
};

export const getRootTaskRows = (tasks = []) => buildTaskTreeRows(tasks).filter((task) => task.isRoot);

export const getSiblingTaskRows = (tasks = [], targetTask = null) => {
    const treeRows = buildTaskTreeRows(tasks);
    const targetNodeId = getTaskNodeId(targetTask);
    const targetRow = treeRows.find((task) => task.treeNodeId === targetNodeId);

    if (!targetRow) {
        return [];
    }

    const parentNodeId = targetRow.treePath?.at(-2) || null;

    return treeRows.filter((task) => (task.treePath?.at(-2) || null) === parentNodeId);
};

export const canMoveTaskWithinParent = (tasks = [], targetTask = null, direction = 'up') => {
    const siblingRows = getSiblingTaskRows(tasks, targetTask);
    const targetNodeId = getTaskNodeId(targetTask);
    const currentIndex = siblingRows.findIndex((task) => task.treeNodeId === targetNodeId);

    if (currentIndex === -1) {
        return false;
    }

    if (direction === 'down') {
        return currentIndex < siblingRows.length - 1;
    }

    return currentIndex > 0;
};

export const buildTaskSiblingReorderUpdates = (tasks = [], targetTask = null, direction = 'up') => {
    const siblingRows = getSiblingTaskRows(tasks, targetTask);
    const targetNodeId = getTaskNodeId(targetTask);
    const currentIndex = siblingRows.findIndex((task) => task.treeNodeId === targetNodeId);

    if (currentIndex === -1) {
        return [];
    }

    const nextIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;

    if (nextIndex < 0 || nextIndex >= siblingRows.length) {
        return [];
    }

    const reorderedSiblingRows = moveItem(siblingRows, currentIndex, nextIndex);

    return reorderedSiblingRows
        .map((taskRow, siblingIndex) => ({
            id: taskRow?.id,
            positionByParent: siblingIndex + 1
        }))
        .filter((taskUpdate) => {
            const currentTask = siblingRows.find((taskRow) => taskRow?.id === taskUpdate.id);
            return Number(currentTask?.positionByParent) !== taskUpdate.positionByParent;
        });
};

export const findTaskFlatIndex = (tasks = [], targetTask = null) => {
    const targetNodeId = getTaskNodeId(targetTask);
    const matchedRow = buildTaskTreeRows(tasks).find((task) => task.treeNodeId === targetNodeId);

    return matchedRow?.flatIndex ?? -1;
};

export const collectTaskBranchIndexes = (tasks = [], targetTask = null) => {
    const treeRows = buildTaskTreeRows(tasks);
    const targetNodeId = getTaskNodeId(targetTask);
    const targetRow = treeRows.find((task) => task.treeNodeId === targetNodeId);

    if (!targetRow) {
        return [];
    }

    return treeRows
        .filter((task) => targetRow.treePath.every((segment, index) => task.treePath[index] === segment))
        .map((task) => task.flatIndex)
        .sort((indexA, indexB) => indexA - indexB);
};

export const deleteTaskBranch = (tasks = [], targetTask = null) => {
    const branchIndexes = new Set(collectTaskBranchIndexes(tasks, targetTask));

    return (Array.isArray(tasks) ? tasks : []).filter((_, flatIndex) => !branchIndexes.has(flatIndex));
};

export const getNextTaskPositionByParent = (tasks = [], parentTask = null) => {
    const parentRef = parentTask ? getTaskNodeId(parentTask) : null;
    const siblingRows = buildTaskTreeRows(tasks).filter((taskRow) => {
        const taskParentRef = getTaskParentRef(taskRow);

        if (!parentRef) {
            return !taskParentRef;
        }

        return taskParentRef === parentRef;
    });

    if (siblingRows.length === 0) {
        return 1;
    }

    return (
        siblingRows.reduce((maxPosition, taskRow) => {
            const position = Number(taskRow?.positionByParent);

            return Number.isFinite(position) && position > maxPosition ? position : maxPosition;
        }, 0) + 1
    );
};
